import React, { useEffect, useState } from 'react';
import ConfirmModal from '../common/modal/ConfirmModal';
import CorpAppSettings from './CorpAppSettings';
import { useDaumPostcodePopup } from 'react-daum-postcode';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Checkbox,
  Grid,
  Typography,
  RadioGroup,
  FormControlLabel,
  Radio,
  Switch,
  Tabs,
  Tab,
} from '@mui/material';
import { useToast } from 'use-toast-mui';
import {
  getManagerListDetail,
  updateManager,
  updateCenterPlanType,
  updateCenterFacilityControl,
  updateCenterKioskLogin,
  updateCenterGolf,
  updateCenterAutoDeduction,
  updateCenterAppBranding,
} from '../../api/member-management';
import { toggleBranchExposure, updateBranchDoorSystem } from '../../api/exposure';
import { getCoordinatesByAddress } from '../../api/nominatium';
import { accountStatus, appBrandingTypes, businessDivision, koreanBanks, planTypes } from '../../constants/constants';
import { FormInputPhone } from '../../form-components/FormInputPhone';
import { FormInputRadio } from '../../form-components/FormInputRadio';
import { FormInputText } from '../../form-components/FormInputText';
import { FormInputDropdown } from '../../form-components/FormInputDropdown';
import { FormInputBRN } from '../../form-components/FormInputBRN';
import AnimationPage from '../../pages/AnimationPage';
import { errorMessages, isKoreanOnly } from '../../utils/methods';

export default function CorpManagementDetail() {
  const { t } = useTranslation();
  const toast = useToast();
  const token = useSelector(function (state) {
    return state.user.user.token;
  });
  const navigate = useNavigate();

  const { id } = useParams();

  const [postalCode, setPostalCode] = useState('');
  const [city, setCity] = useState('');
  const [planType, setPlanType] = useState('');
  const [centerId, setCenterId] = useState(null);
  const [exposureYn, setExposureYn] = useState('N');
  const [facilityControlYn, setFacilityControlYn] = useState('N');
  // 안면인식 출입문 체계(#91) — 시설 제어(IoT)와 별개 플래그, 지점 단위
  const [doorSystemYn, setDoorSystemYn] = useState('N');
  const [branchId, setBranchId] = useState(null);
  const [kioskLoginYn, setKioskLoginYn] = useState('N');
  // 골프 관리(타석 관리 메뉴·매장관리 골프 탭) 노출 — 기업회원 단위
  const [golfYn, setGolfYn] = useState('N');
  // 수강권 자동 차감 설정 노출 — 기업회원 단위
  const [autoDeductionYn, setAutoDeductionYn] = useState('N');
  const [appBrandingType, setAppBrandingType] = useState('NONE');
  // 같은 계열(GROUP)일 때 직영점·가맹점(자기 말고 다른 가맹점) 보이기. 자기 지점은 항상 보인다
  const [showDirect, setShowDirect] = useState(true);
  const [showFranchise, setShowFranchise] = useState(true);
  const [tab, setTab] = useState(0);
  // 브랜딩을 켜거나 범위를 바꾸면(좁히면) 이 지점 연동 회원의 다른 센터 연동이 해제되므로 확인을 받는다(#186)
  // { type, scope } — scope 는 GROUP 직영점·가맹점 보이기 변경분
  const [pendingAppBranding, setPendingAppBranding] = useState(null);

  const { handleSubmit, control, setValue, setError, reset, getValues, watch } = useForm({});

  const scriptUrl = 'https://t1.daumcdn.net/mapjsapi/bundle/postcode/prod/postcode.v2.js';
  const open = useDaumPostcodePopup(scriptUrl);

  const handleComplete = async data => {
    setCity(data.sigungu);
    setValue('address', data.jibunAddress);
    setPostalCode(data.zonecode);

    await getCoordinatesByAddress(data.addressEnglish).then(data => {
      setValue('latitude', data[0].coordinate.latitude);
      setValue('longitude', data[0].coordinate.longitude);
    });
  };

  const handleClick = () => {
    open({ onComplete: handleComplete });
  };

  useEffect(() => {
    getCorpDetail();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getCorpDetail = async () => {
    try {
      await getManagerListDetail(token, id).then(res => {
        reset({
          name: res?.managerInfo?.name,
          username: res?.managerInfo?.username,
          phoneNumber: res?.managerInfo?.phoneNumber,
          email: res?.managerInfo?.email,
          status: res?.managerInfo?.status,
          businessName: res?.centerInfo?.businessName,
          businessNumber: res?.centerInfo?.businessNumber,
          representativeNumber: res?.centerInfo?.representativeNumber,
          email_1: res?.centerInfo?.email,
          businessClassification: res?.centerInfo?.businessClassification,
          homepage: res?.centerInfo?.homepage,
          businessType: res?.centerInfo?.businessType,
          bankName: res?.centerInfo?.bankName || '',
          accountNumber: res?.centerInfo?.accountNumber || '',
          accountHolder: res?.centerInfo?.accountHolder || '',
          address: res?.centerInfo?.address,
          addressDetail: res?.centerInfo?.addressDetail,
        });
        setCenterId(res?.centerInfo?.id);
        setPlanType(res?.centerInfo?.planType || 'LITE');
        setExposureYn(res?.centerInfo?.exposureYn || 'N');
        setFacilityControlYn(res?.centerInfo?.facilityControlYn || 'N');
        setDoorSystemYn(res?.centerInfo?.doorSystemYn || 'N');
        setBranchId(res?.centerInfo?.branchId ?? null);
        setKioskLoginYn(res?.centerInfo?.kioskLoginYn || 'N');
        setGolfYn(res?.centerInfo?.golfYn || 'N');
        setAutoDeductionYn(res?.centerInfo?.autoDeductionYn || 'N');
        setAppBrandingType(res?.centerInfo?.appBrandingType || 'NONE');
        setShowDirect(res?.centerInfo?.appBrandingShowDirectYn !== 'N');
        setShowFranchise(res?.centerInfo?.appBrandingShowFranchiseYn !== 'N');
      });
    } catch (error) {
      toast.error(errorMessages(error.message));
    }
  };

  const submit = async values => {
    if (!isKoreanOnly(values.name)) {
      toast.error('이름은 한글만 가능합니다');
      return;
    }

    if (values.password !== values.verifyPassword) {
      return setError('verifyPassword', {
        type: 'custom',
        message: t('Password not matched'),
      });
    }

    const data = {
      managerInfo: {
        name: values.name,
        password: values.password,
        phoneNumber: values.phoneNumber,
        email: values.email,
      },
      centerInfo: {
        businessName: values.businessName,
        businessNumber: values.businessNumber,
        businessClassification: values.businessClassification,
        businessType: values.businessType,
        bankName: values.bankName,
        accountNumber: values.accountNumber,
        accountHolder: values.accountHolder,
        postalCode: postalCode,
        city: city,
        address: values.address,
        addressDetail: values.addressDetail,
        representativeNumber: values.representativeNumber,
        email: values.email_1,
        homepage: values.homepage,
        latitude: values.latitude,
        longitude: values.longitude,
      },
      status: values.status,
    };

    try {
      await updateManager(token, id, data);
      toast.success(t('Successfully updated'));
    } catch (error) {
      toast.error(errorMessages(error));
    }
  };

  const handleExposureToggle = async () => {
    if (!centerId) {
      toast.error('센터 정보가 없습니다');
      return;
    }
    const newValue = exposureYn === 'Y' ? 'N' : 'Y';
    setExposureYn(newValue);
    try {
      await toggleBranchExposure(token, centerId, newValue);
      toast.success(newValue === 'Y' ? '앱에 노출됩니다' : '앱에서 숨김 처리됩니다');
    } catch (error) {
      setExposureYn(exposureYn);
      toast.error(errorMessages(error));
    }
  };

  // 안면인식 출입문 체계 토글 — 켠 지점만 시설 제어 > 안면 인식 그룹에서 '출입문 체계로 전환'이 열린다.
  const handleDoorSystemChange = async checked => {
    if (!branchId) {
      toast.error('지점 정보가 없습니다');
      return;
    }
    const nextValue = checked ? 'Y' : 'N';
    try {
      await updateBranchDoorSystem(token, branchId, nextValue);
      setDoorSystemYn(nextValue);
      toast.success(
        nextValue === 'Y' ? '출입문 체계를 켰습니다. 안면 인식 그룹에서 전환할 수 있습니다' : '출입문 체계를 껐습니다',
      );
    } catch (error) {
      toast.error(errorMessages(error));
    }
  };

  // 시설 제어(IoT) 메뉴 노출 토글 — Plan Type과 같이 즉시 저장한다.
  const handleFacilityControlChange = async checked => {
    if (!centerId) {
      toast.error(t('Center ID not found'));
      return;
    }
    const nextValue = checked ? 'Y' : 'N';
    try {
      await updateCenterFacilityControl(token, centerId, nextValue);
      setFacilityControlYn(nextValue);
      toast.success('시설 제어 노출 설정이 변경되었습니다. 해당 계정은 재로그인 후 반영됩니다');
    } catch (error) {
      toast.error(errorMessages(error));
    }
  };

  const handleKioskLoginChange = async checked => {
    if (!centerId) {
      toast.error(t('Center ID not found'));
      return;
    }
    const nextValue = checked ? 'Y' : 'N';
    try {
      await updateCenterKioskLogin(token, centerId, nextValue);
      setKioskLoginYn(nextValue);
      toast.success(
        nextValue === 'Y'
          ? '체크인앱 로그인이 승인되었습니다'
          : '체크인앱 로그인 승인이 취소되었습니다. 다음 로그인부터 차단됩니다',
      );
    } catch (error) {
      toast.error(errorMessages(error));
    }
  };

  const handleAutoDeductionChange = async checked => {
    if (!centerId) {
      toast.error(t('Center ID not found'));
      return;
    }
    const nextValue = checked ? 'Y' : 'N';
    try {
      await updateCenterAutoDeduction(token, centerId, nextValue);
      setAutoDeductionYn(nextValue);
      toast.success(
        nextValue === 'Y'
          ? '자동 차감이 켜졌습니다. 매니저는 재로그인 후 반영됩니다'
          : '자동 차감이 꺼졌습니다. 매니저는 재로그인 후 반영됩니다',
      );
    } catch (error) {
      toast.error(errorMessages(error));
    }
  };

  const handleAppBrandingChange = async (nextValue, scope = {}) => {
    if (!centerId) {
      toast.error(t('Center ID not found'));
      return;
    }
    try {
      await updateCenterAppBranding(token, centerId, nextValue, scope);
      setAppBrandingType(nextValue);
      if (scope.showDirect !== undefined) setShowDirect(scope.showDirect);
      if (scope.showFranchise !== undefined) setShowFranchise(scope.showFranchise);
      toast.success('회원앱 브랜딩 설정이 변경되었습니다');
    } catch (error) {
      toast.error(errorMessages(error));
    }
  };

  const handleGolfChange = async checked => {
    if (!centerId) {
      toast.error(t('Center ID not found'));
      return;
    }
    const nextValue = checked ? 'Y' : 'N';
    try {
      await updateCenterGolf(token, centerId, nextValue);
      setGolfYn(nextValue);
      toast.success(
        nextValue === 'Y'
          ? '골프 관리가 켜졌습니다. 매니저는 재로그인 후 반영됩니다'
          : '골프 관리가 꺼졌습니다. 매니저는 재로그인 후 반영됩니다',
      );
    } catch (error) {
      toast.error(errorMessages(error));
    }
  };

  const handlePlanTypeChange = async newPlanType => {
    if (!centerId) {
      toast.error(t('Center ID not found'));
      return;
    }
    try {
      await updateCenterPlanType(token, centerId, newPlanType);
      setPlanType(newPlanType);
      toast.success(t('Plan type updated successfully'));
    } catch (error) {
      toast.error(errorMessages(error));
    }
  };

  return (
    <AnimationPage>
      <Box sx={{ background: 'white', borderRadius: '5px', width: '100%', px: 2.6, py: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <Link to="/corp" style={{ textDecoration: 'none', color: '#3a3a3a', display: 'flex' }}>
            <img src="/images/backIcon.svg" alt="backIcon" />
            <Typography sx={{ fontSize: '18px', lineHeight: '24px', ml: '8px', fontWeight: 700 }}>
              {t('Corporate Member Details')}
            </Typography>
          </Link>

          <Link to="/corp" style={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}>
            <Typography sx={{ ml: 0.75, color: '#0275FC', fontSize: '14px', fontWeight: 500, lineHeight: '16px' }}>
              {t('List')}
            </Typography>
          </Link>
        </Box>
        {/* 브랜딩 지점만 '앱 설정'(회원앱 홈 배너) 탭이 생긴다 */}
        {appBrandingType !== 'NONE' && (
          <Tabs value={tab} onChange={(_, value) => setTab(value)} sx={{ mb: 1 }}>
            <Tab label="기본 정보" />
            <Tab label="앱 설정" />
          </Tabs>
        )}
        <hr />
        {tab === 1 && appBrandingType !== 'NONE' && <CorpAppSettings centerId={centerId} />}
        {(tab === 0 || appBrandingType === 'NONE') && (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', mt: '30px' }}>
              <Typography sx={{ width: '30%', fontSize: '16px', fontWeight: 500, mr: '10px' }}>
                {t('Representative Information')}
              </Typography>
            </Box>

            <Grid container columnSpacing={9} sx={{ mt: 3 }}>
              <Grid item xs={6}>
                <Box className="detail-form-box">
                  <Grid item xs={2}>
                    <Typography className="detail-form-label">{t('Name*')}</Typography>
                  </Grid>
                  <Grid item xs={10}>
                    <FormInputText
                      control={control}
                      name="name"
                      placeholder={t('Please enter your name')}
                      rules={{ required: true }}
                    />
                  </Grid>
                </Box>
                <Box className="detail-form-box" my={3.25}>
                  <Grid item xs={2}>
                    <Typography className="detail-form-label">{t('ID*')}</Typography>
                  </Grid>
                  <Grid item xs={10}>
                    <FormInputText disabled name="username" control={control} placeholder={t('Please enter your ID')} />
                  </Grid>
                </Box>

                <Box className="detail-form-box">
                  <Grid item xs={2}>
                    <Typography className="detail-form-label">{t('Password')}</Typography>
                  </Grid>
                  <Grid item xs={10}>
                    <FormInputText
                      control={control}
                      type="password"
                      autoComplete="new-password"
                      sx={{ background: '#fff', borderRadius: '4px' }}
                      name="password"
                      placeholder={t(
                        'Please enter your password (8 to 16 characters including English letters, numbers, and special characters)',
                      )}
                      rules={{
                        pattern: {
                          value: /^(?=.*[A-Za-z])(?=.*\d)(?=.*[~!@#$%^&*()_=])[A-Za-z\d~!@#$%^&*()_=]{8,16}$/,
                          message: t(
                            'Please enter your password (8 to 16 characters including English letters, numbers, and special characters)',
                          ),
                        },
                      }}
                    />
                  </Grid>
                </Box>
                <Box className="detail-form-box" my={3.25}>
                  <Grid item xs={2}>
                    <Typography className="detail-form-label">{t('Confirm Password')}</Typography>
                  </Grid>
                  <Grid item xs={10}>
                    <FormInputText
                      control={control}
                      name="verifyPassword"
                      autoComplete="new-password"
                      sx={{ color: '#9A9A9A', background: '#fff' }}
                      placeholder={t(
                        'Please enter your password (8 to 16 characters including English letters, numbers, and special characters)',
                      )}
                      type="password"
                      rules={{
                        // required: t('Password not matched'),
                        validate: value => value === getValues('password') || t('Password not matched'),
                      }}
                    />
                  </Grid>
                </Box>
              </Grid>

              <Grid item xs={6}>
                <Box className="detail-form-box">
                  <Grid item xs={2}>
                    <Typography className="detail-form-label">{t('Phone number*')}</Typography>
                  </Grid>
                  <Grid item xs={10}>
                    <FormInputPhone
                      control={control}
                      setValue={setValue}
                      name="phoneNumber"
                      rules={{ required: true }}
                      placeholder={t('Enter the phone number')}
                    />
                  </Grid>
                  {/* <Grid item xs={2}>
                <Button
                  variant="contained"
                  color="primary"
                  sx={{
                    height: 40,
                    bgcolor: '#F8FBFB',
                    ml: 2,
                    boxShadow: 'none',
                    color: '#0F7F85',
                    fontSize: '13px',
                    fontWeight: 500,
                    lineHeight: '15.51px',
                    '&:hover': { color: '#fff' },
                  }}>
                  {t('SENT')}
                </Button>
              </Grid> */}
                </Box>

                {/* <Box className="detail-form-box" my={3.25}>
              <Grid item xs={2}>
                <Typography className="detail-form-label">{t('Authentication Verification')}</Typography>
              </Grid>
              <Grid item xs={10}>
                <FormInputText
                  name="certification"
                  control={control}
                  placeholder={t('Please enter your verification number')}
                />
              </Grid>
              {/* <Grid item xs={2}>
                <Button
                  variant="contained"
                  color="primary"
                  sx={{
                    height: 40,
                    bgcolor: '#F8FBFB',
                    ml: 2,
                    boxShadow: 'none',
                    color: '#0F7F85',
                    fontSize: '13px',
                    fontWeight: 500,
                    lineHeight: '15.51px',
                    '&:hover': { color: '#fff' },
                  }}>
                  {t('Authentication')}
                </Button>
              </Grid>
            </Box> */}
                <Box className="detail-form-box" my={3.25}>
                  <Grid item xs={2}>
                    <Typography className="detail-form-label">{t('E-mail*')}</Typography>
                  </Grid>
                  <Grid item xs={10}>
                    <FormInputText
                      name="email"
                      control={control}
                      type="email"
                      autoComplete="off"
                      rules={{ required: true }}
                      placeholder={t('Please enter your email')}
                    />
                  </Grid>
                </Box>
              </Grid>
            </Grid>
            <hr />

            <Box sx={{ display: 'flex', alignItems: 'center', mt: '30px' }}>
              <Typography sx={{ width: '30%', fontSize: '16px', fontWeight: 500, mr: '10px' }}>
                {t('Business Information')}
              </Typography>
            </Box>
            <Grid container columnSpacing={9} sx={{ my: 5 }}>
              <Grid item xs={6}>
                <Box className="detail-form-box">
                  <Grid item xs={2}>
                    <Typography className="detail-form-box">{t('Business Name*')}</Typography>
                  </Grid>
                  <Grid item xs={10}>
                    <FormInputBRN
                      name="businessName"
                      rules={{ required: true }}
                      control={control}
                      placeholder={t('Please enter your business name')}
                    />
                  </Grid>
                </Box>

                <Box className="detail-form-box" my={3.25}>
                  <Grid item xs={2}>
                    <Typography className="detail-form-box">{t('Employer Identification Number*')}</Typography>
                  </Grid>
                  <Grid item xs={10}>
                    <FormInputBRN
                      name="businessNumber"
                      rules={{ required: true }}
                      setValue={setValue}
                      control={control}
                      placeholder={t('Please enter your business number')}
                    />
                  </Grid>
                </Box>

                <Box className="detail-form-box">
                  <Grid item xs={2}>
                    <Typography className="detail-form-box">{t('Employer Type')}</Typography>
                  </Grid>
                  <Grid item xs={10}>
                    <FormInputRadio
                      rules={{ required: true }}
                      name="businessClassification"
                      control={control}
                      options={businessDivision}
                      row
                    />
                  </Grid>
                </Box>
                <Box className="detail-form-box" my={3.25}>
                  <Grid item xs={2}>
                    <Typography className="detail-form-box">은행명</Typography>
                  </Grid>
                  <Grid item xs={10}>
                    <FormInputDropdown name="bankName" control={control} options={koreanBanks} sx={{ width: '100%' }} />
                  </Grid>
                </Box>
                <Box className="detail-form-box" my={3.25}>
                  <Grid item xs={2}>
                    <Typography className="detail-form-box">계좌번호</Typography>
                  </Grid>
                  <Grid item xs={10}>
                    <FormInputText
                      name="accountNumber"
                      control={control}
                      placeholder="계좌번호를 입력해 주세요"
                      onChange={e => {
                        const filtered = e.target.value.replace(/[^0-9-]/g, '');
                        setValue('accountNumber', filtered);
                      }}
                    />
                  </Grid>
                </Box>
                <Box className="detail-form-box" my={3.25}>
                  <Grid item xs={2}>
                    <Typography className="detail-form-box">예금주</Typography>
                  </Grid>
                  <Grid item xs={10}>
                    <FormInputText
                      name="accountHolder"
                      control={control}
                      placeholder="예금주를 입력해 주세요"
                      onChange={e => {
                        const filtered = e.target.value.replace(/[^가-힣a-zA-Zㄱ-ㅎㅏ-ㅣ\s]/g, '');
                        setValue('accountHolder', filtered);
                      }}
                    />
                  </Grid>
                </Box>

                <Box className="detail-form-box" my={3.25}>
                  <Grid item xs={2}>
                    <Typography className="detail-form-box">{t('Type of Business*')}</Typography>
                  </Grid>
                  <Grid item xs={10}>
                    <FormInputText
                      name="businessType"
                      control={control}
                      rules={{ required: true }}
                      placeholder={t('Please enter the industry/type of business')}
                    />
                  </Grid>
                </Box>

                <Box className="detail-form-box" my={3.25}>
                  <Grid item xs={2}>
                    <Typography className="detail-form-box">{t('Address*')}</Typography>
                  </Grid>
                  <Grid item xs={8}>
                    <FormInputText
                      name="address"
                      control={control}
                      rules={{ required: true }}
                      placeholder={t('Please enter your address')}
                    />
                  </Grid>
                  <Grid item xs={2}>
                    <Button
                      onClick={handleClick}
                      variant="contained"
                      color="primary"
                      sx={{
                        height: 40,
                        bgcolor: '#F8FBFB',
                        boxShadow: 'none',
                        color: '#0F7F85',
                        fontSize: '13px',
                        fontWeight: 500,
                        lineHeight: '15.51px',
                        ml: 0,
                        width: '100%',
                        '&:hover': { color: '#fff' },
                      }}>
                      {t('Search')}
                    </Button>
                  </Grid>
                </Box>

                <Box className="detail-form-box">
                  <Grid item xs={2}></Grid>
                  <Grid item xs={10}>
                    <FormInputText
                      name="addressDetail"
                      control={control}
                      placeholder={t('Please enter your detailed address')}
                    />
                  </Grid>
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Box className="detail-form-box">
                  <Grid item xs={2.5}>
                    <Typography className="detail-form-box">{t('Representative number*')}</Typography>
                  </Grid>
                  <Grid item xs={10}>
                    <FormInputPhone
                      control={control}
                      setValue={setValue}
                      rules={{ required: true }}
                      name="representativeNumber"
                      placeholder={t('Please enter your representative number')}
                      isLandline
                    />
                  </Grid>
                </Box>

                {/* <Box sx={{ display: 'flex', alignItems: 'center', my: 3.25 }}>
              <Grid item xs={2.5}>
                <Typography className="detail-form-box">{t('E-mail*')}</Typography>
              </Grid>
              <Grid item xs={9.5}>
                <FormInputText
                  name="email_1"
                  control={control}
                  type="email"
                  rules={{ required: true }}
                  placeholder={t('Please enter your email')}
                />
              </Grid>
            </Box> */}
                <Box className="detail-form-box" sx={{ display: 'flex', alignItems: 'center', my: 3.25 }}>
                  <Grid item xs={2.5}>
                    <Typography className="detail-form-box">{t('Homepage')}</Typography>
                  </Grid>
                  <Grid item xs={10}>
                    <FormInputText name="homepage" control={control} placeholder={t('Home page URL')} />
                  </Grid>
                </Box>
              </Grid>
            </Grid>
            <hr />
            <Box sx={{ display: 'flex', my: 4 }}>
              <Typography className="detail-form-box" sx={{ width: 70, mr: 3 }}>
                {t('Account Status')}
              </Typography>

              <FormInputRadio name="status" control={control} options={accountStatus} row />
            </Box>
            {/* <hr />
        <Box sx={{ display: 'flex', alignItems: 'center', my: 4 }}>
          <Typography className="detail-form-box" sx={{ width: 70, mr: 3 }}>
            앱 노출
          </Typography>
          <Switch
            size="small"
            checked={exposureYn === 'Y'}
            onChange={handleExposureToggle}
            sx={{
              '& .MuiSwitch-switchBase.Mui-checked': { color: '#4AB3BC' },
              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#4AB3BC' },
            }}
          />
          <Typography sx={{ ml: 1, fontSize: '13px', color: '#666' }}>
            {exposureYn === 'Y' ? '노출' : '숨김'}
          </Typography>
        </Box> */}
            <hr />
            <Box sx={{ display: 'flex', my: 4 }}>
              <Typography className="detail-form-box" sx={{ width: 100, mr: 3 }}>
                {t('Plan Type')}
              </Typography>
              <RadioGroup row value={planType} onChange={e => handlePlanTypeChange(e.target.value)}>
                {planTypes.map(option => (
                  <FormControlLabel
                    key={option.value}
                    value={option.value}
                    control={<Radio />}
                    label={t(option.label)}
                  />
                ))}
              </RadioGroup>
            </Box>
            <hr />
            <Box sx={{ display: 'flex', alignItems: 'center', my: 4 }}>
              <Typography className="detail-form-box" sx={{ width: 100, mr: 3 }}>
                시설 제어
              </Typography>
              <Switch
                checked={facilityControlYn === 'Y'}
                onChange={e => handleFacilityControlChange(e.target.checked)}
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': { color: '#4AB3BC' },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#4AB3BC' },
                }}
              />
              <Typography sx={{ ml: 1, fontSize: '13px', color: '#666' }}>
                {facilityControlYn === 'Y'
                  ? 'IoT 시설 제어 메뉴 노출 (해당 계정 재로그인 후 반영)'
                  : 'IoT 시설 제어 메뉴 숨김'}
              </Typography>
            </Box>
            <hr />
            <Box sx={{ display: 'flex', alignItems: 'center', my: 4 }}>
              <Typography className="detail-form-box" sx={{ width: 100, mr: 3 }}>
                출입문 체계
              </Typography>
              <Switch
                checked={doorSystemYn === 'Y'}
                onChange={e => handleDoorSystemChange(e.target.checked)}
                disabled={!branchId}
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': { color: '#4AB3BC' },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#4AB3BC' },
                }}
              />
              <Typography sx={{ ml: 1, fontSize: '13px', color: '#666' }}>
                {doorSystemYn === 'Y'
                  ? "안면인식 출입문 체계 사용 — 안면 인식 그룹에서 '출입문 체계로 전환' 가능 (전환은 되돌릴 수 없음)"
                  : '안면인식 출입문 체계 미사용 (기존 운동종목 출입그룹 방식)'}
              </Typography>
            </Box>
            <hr />
            <Box sx={{ display: 'flex', alignItems: 'center', my: 4 }}>
              <Typography className="detail-form-box" sx={{ width: 100, mr: 3 }}>
                체크인앱 로그인
              </Typography>
              <Switch
                checked={kioskLoginYn === 'Y'}
                onChange={e => handleKioskLoginChange(e.target.checked)}
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': { color: '#4AB3BC' },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#4AB3BC' },
                }}
              />
              <Typography sx={{ ml: 1, fontSize: '13px', color: '#666' }}>
                {kioskLoginYn === 'Y'
                  ? '이 기업회원(매니저) 계정으로 체크인앱 로그인 허용'
                  : '체크인앱 로그인 차단 (승인 필요 안내가 표시됨)'}
              </Typography>
            </Box>
            <hr />
            <Box sx={{ display: 'flex', alignItems: 'center', my: 4 }}>
              <Typography className="detail-form-box" sx={{ width: 100, mr: 3 }}>
                골프 관리
              </Typography>
              <Switch
                checked={golfYn === 'Y'}
                onChange={e => handleGolfChange(e.target.checked)}
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': { color: '#4AB3BC' },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#4AB3BC' },
                }}
              />
              <Typography sx={{ ml: 1, fontSize: '13px', color: '#666' }}>
                {golfYn === 'Y'
                  ? '골프 타석 관리 메뉴·매장관리 골프 탭 노출 (매니저는 재로그인 후 반영)'
                  : '골프 관리 숨김 (슈퍼관리자만 사용)'}
              </Typography>
            </Box>
            <hr />
            <Box sx={{ display: 'flex', alignItems: 'center', my: 4 }}>
              <Typography className="detail-form-box" sx={{ width: 100, mr: 3 }}>
                자동 차감
              </Typography>
              <Switch
                checked={autoDeductionYn === 'Y'}
                onChange={e => handleAutoDeductionChange(e.target.checked)}
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': { color: '#4AB3BC' },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#4AB3BC' },
                }}
              />
              <Typography sx={{ ml: 1, fontSize: '13px', color: '#666' }}>
                {autoDeductionYn === 'Y'
                  ? '수강권 등록·수정 화면에 자동 차감 설정 노출 (매니저는 재로그인 후 반영)'
                  : '자동 차감 설정 숨김 (슈퍼관리자만 사용)'}
              </Typography>
            </Box>
            <hr />
            <Box sx={{ display: 'flex', alignItems: 'center', my: 4 }}>
              <Typography className="detail-form-box" sx={{ width: 100, mr: 3 }}>
                회원앱 브랜딩
              </Typography>
              <RadioGroup
                row
                value={appBrandingType}
                onChange={e => {
                  const nextValue = e.target.value;
                  if (nextValue === 'NONE') handleAppBrandingChange(nextValue);
                  else setPendingAppBranding({ type: nextValue, scope: {} });
                }}>
                {appBrandingTypes.map(option => (
                  <FormControlLabel key={option.value} value={option.value} control={<Radio />} label={option.label} />
                ))}
              </RadioGroup>
              <Typography sx={{ ml: 1, fontSize: '13px', color: '#666' }}>
                {appBrandingType === 'SELF' && '이 지점과 연동한 회원앱에는 이 지점만 보임'}
                {appBrandingType === 'GROUP' && '이 지점과 연동한 회원앱에는 이 지점과 아래에서 고른 같은 계열 지점이 보임'}
                {appBrandingType === 'NONE' && '회원앱 브랜딩 미사용'}
              </Typography>
            </Box>
            {appBrandingType === 'GROUP' && (
              <Box sx={{ display: 'flex', alignItems: 'center', mt: -2, mb: 4, ml: '124px' }}>
                {[
                  { key: 'showDirect', label: '직영점 보이기', checked: showDirect },
                  { key: 'showFranchise', label: '가맹점 보이기 (이 지점 말고 다른 가맹점)', checked: showFranchise },
                ].map(option => (
                  <FormControlLabel
                    key={option.key}
                    label={option.label}
                    control={
                      <Checkbox
                        checked={option.checked}
                        onChange={e => {
                          const scope = { [option.key]: e.target.checked };
                          // 넓히면 끊길 연동이 없어 바로, 좁히면 연동 해제 확인
                          if (e.target.checked) handleAppBrandingChange('GROUP', scope);
                          else setPendingAppBranding({ type: 'GROUP', scope });
                        }}
                      />
                    }
                  />
                ))}
              </Box>
            )}
            <hr />
            <Box sx={{ display: 'flex', justifyContent: 'end', mt: 3 }}>
              <Button
                href="/corp"
                variant="contained"
                sx={{
                  borderRadius: '5px',
                  minWidth: '103px',
                  height: '40px',
                  bgcolor: '#98A6AD',
                  color: '#fff',
                  '&:hover': { background: '#ddd' },
                  fontSize: '13px',
                  fontWeight: 700,
                }}>
                {t('Back')}
              </Button>
              <Button
                id="store-create-button"
                onClick={handleSubmit(submit)}
                // disabled={user.userPoint}
                variant="contained"
                color="primary"
                sx={{ borderRadius: '5px', minWidth: '183px', height: '40px', ml: 1.25, fontSize: '13px' }}>
                {t('Save')}
              </Button>
            </Box>
          </>
        )}
      </Box>

      <ConfirmModal
        open={!!pendingAppBranding}
        onClose={() => setPendingAppBranding(null)}
        onConfirm={() => {
          const { type, scope } = pendingAppBranding;
          setPendingAppBranding(null);
          handleAppBrandingChange(type, scope);
        }}
        message={`회원앱 브랜딩: ${appBrandingTypes.find(option => option.value === pendingAppBranding?.type)?.label || ''}\n\n바꾸면 이 지점과 연동한 회원의 다른 센터 연동이 해제됩니다.\n구독 중인 센터 연동은 유지됩니다.\n회원 안내는 센터에서 해 주세요.`}
        cancelText="취소"
        confirmText="변경"
        confirmColor="#4AB3BC"
      />
    </AnimationPage>
  );
}
